import React, { useEffect, useState } from "react";
import {
  fetchAiLiteracyTutorBanner,
  fetchAiLiteracyTutorIntroduction,
  fetchAiLiteracyTutorMomentHighlight,
  fetchAiLiteracyTutorTutors,
  fetchAiLiteracyTutorLearningPackages,
} from "../services/aiLiteracyTutorService";
import {
  AiLiteracyTutorBanner,
  AiLiteracyTutorIntroduction,
  AiLiteracyTutorMomentHighlight,
  AiLiteracyTutorTutor,
  AiLiteracyTutorLearningPackage,
} from "../types";

const AiLiteracyTutor: React.FC = () => {
  const [banner, setBanner] = useState<AiLiteracyTutorBanner | null>(null);
  const [intro, setIntro] = useState<AiLiteracyTutorIntroduction | null>(null);
  const [momentHighlight, setMomentHighlight] =
    useState<AiLiteracyTutorMomentHighlight | null>(null);
  const [tutors, setTutors] = useState<AiLiteracyTutorTutor[]>([]);
  const [learningPackages, setLearningPackages] = useState<
    AiLiteracyTutorLearningPackage[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add("page-ai-literacy-tutor");

    const loadData = async () => {
      try {
        const [bannerData, introData, momentData, tutorsData, packagesData] =
          await Promise.all([
            fetchAiLiteracyTutorBanner().catch((e) => {
              console.error("Banner fetch failed:", e);
              return null;
            }),
            fetchAiLiteracyTutorIntroduction().catch((e) => {
              console.error("Intro fetch failed:", e);
              return null;
            }),
            fetchAiLiteracyTutorMomentHighlight().catch((e) => {
              console.error("Moment highlight fetch failed:", e);
              return null;
            }),
            fetchAiLiteracyTutorTutors().catch((e) => {
              console.error("Tutors fetch failed:", e);
              return [];
            }),
            fetchAiLiteracyTutorLearningPackages().catch((e) => {
              console.error("Packages fetch failed:", e);
              return [];
            }),
          ]);

        setBanner(bannerData);
        setIntro(introData);
        setMomentHighlight(momentData);
        setTutors(tutorsData);
        setLearningPackages(packagesData);
      } catch (error) {
        console.error("Error fetching AI Literacy Tutor page data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    return () => {
      document.body.classList.remove("page-ai-literacy-tutor");
    };
  }, []);

  console.log("banner", banner);
  console.log("intro", intro);
  console.log("momentHighlight", momentHighlight);
  console.log("tutors", tutors);
  console.log("learningPackages", learningPackages);

  if (loading) {
    return (
      <main id="main-content" role="main">
        <section
          className="page-hero"
          style={{
            background:
              "linear-gradient(120deg, #003366 70%, rgba(0,102,204,0.8) 100%)",
            padding: "140px 0 80px 0",
          }}
        >
          <div className="container text-center">
            <h1
              className="text-white"
              style={{ fontSize: "3rem", fontWeight: 900 }}
            >
              AI Literacy Tutor
            </h1>
          </div>
        </section>
        <div className="container py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </main>
    );
  }

  // Helper to extract moments photos
  const momentPhotos: string[] = momentHighlight?.photos || [];

  const heroBgStyle = {
    background: banner?.image
      ? `linear-gradient(135deg, rgba(0,51,102,.75) 0%, rgba(255,153,0,.35) 100%), url('${banner.image}') center/cover no-repeat`
      : "linear-gradient(120deg, #003366 70%, rgba(0,102,204,0.8) 100%)",
    padding: "160px 0 100px 0",
    color: "white",
    textAlign: "center" as const,
    position: "relative" as const,
    overflow: "hidden" as const,
  };

  return (
    <main id="main-content" role="main">
      {/* Banner */}
      <section className="page-hero" style={heroBgStyle}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1
                className="text-white mb-3"
                style={{
                  fontSize: "3.5rem",
                  fontWeight: 900,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                {banner?.title || "AI Literacy Tutor"}
              </h1>
              <p
                className="text-white page-hero-subtitle mx-auto"
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 600,
                  opacity: 0.95,
                  maxWidth: "800px",
                  lineHeight: "1.6",
                  textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              >
                {banner?.subtitle ||
                  "Personalized guidance to build confidence and capability in artificial intelligence."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      {intro && (
        <section className="intro-section py-5 bg-white">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <h2
                  className="mb-4"
                  style={{
                    fontWeight: 800,
                    color: "#1A202C",
                    fontSize: "2.5rem",
                  }}
                >
                  {intro.title || "About the Tutor Program"}
                </h2>
                <p
                  style={{
                    fontSize: "1.1rem",
                    lineHeight: "1.8",
                    color: "#4A5568",
                    whiteSpace: "pre-line",
                  }}
                >
                  {intro.description}
                </p>
              </div>
              {intro.cover_photo && (
                <div className="col-lg-6 text-center">
                  <div
                    style={{
                      borderRadius: "20px",
                      overflow: "hidden",
                      boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src={intro.cover_photo}
                      alt={intro.title}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Moment Highlight */}
      {momentHighlight && (
        <section className="moment-highlights py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2
                style={{
                  fontWeight: 800,
                  color: "#1A202C",
                  fontSize: "2.5rem",
                  marginBottom: "1rem",
                }}
              >
                Moment Highlights
              </h2>
              <div
                style={{
                  width: "80px",
                  height: "3px",
                  backgroundColor: "#7B9EBD",
                  margin: "0 auto",
                }}
              ></div>
            </div>
            {momentHighlight.text && (
              <p
                className="text-center mx-auto mb-5"
                style={{
                  fontSize: "1.1rem",
                  color: "#4A5568",
                  maxWidth: "800px",
                }}
              >
                {momentHighlight.text}
              </p>
            )}
            {momentPhotos.length > 0 && (
              <div className="row g-4 justify-content-center">
                {momentPhotos.map((photo, index) => (
                  <div key={index} className="col-lg-4 col-md-6 col-sm-12">
                    <div
                      className="moment-photo-card"
                      style={{
                        height: "240px",
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                        position: "relative",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      }}
                    >
                      <img
                        src={photo}
                        alt={`Moment ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Our Tutors */}
      {tutors.length > 0 && (
        <section className="our-tutors py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5">
              <h2
                style={{
                  fontWeight: 800,
                  color: "#1A202C",
                  fontSize: "2.5rem",
                  marginBottom: "1rem",
                }}
              >
                Our Tutors
              </h2>
              <div
                style={{
                  width: "80px",
                  height: "3px",
                  backgroundColor: "#7B9EBD",
                  margin: "0 auto",
                }}
              ></div>
            </div>

            <div className="row g-4 mt-2 justify-content-center">
              {tutors.map((tutor) => (
                <div key={tutor.id} className="col-lg-3 col-md-6 col-sm-6">
                  <div
                    className="text-center"
                    style={{
                      padding: "1rem 0",
                      background: "transparent",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <div
                      className="mb-3"
                      style={{
                        width: "200px",
                        maxWidth: "100%",
                        aspectRatio: "3/4",
                        overflow: "hidden",
                        backgroundColor: "transparent",
                      }}
                    >
                      <img
                        src={tutor.profile_photo}
                        alt={tutor.full_name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>

                    <h4
                      style={{
                        fontSize: "1.875rem",
                        fontWeight: 900,
                        color: "#1A202C",
                        marginBottom: "0.4rem",
                        lineHeight: "1.25",
                      }}
                    >
                      {tutor.full_name}
                    </h4>

                    <span
                      style={{
                        fontSize: "1.2rem",
                        color: "#A0AEC0",
                        fontWeight: 600,
                        display: "block",
                        marginBottom: "0.75rem",
                        lineHeight: "1.4",
                        padding: "0 10px",
                      }}
                    >
                      {tutor.degree_and_major}
                    </span>

                    <div
                      style={{
                        width: "40px",
                        height: "1px",
                        backgroundColor: "#FF8C66",
                        margin: "0 auto 0.75rem auto",
                      }}
                    ></div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "24px minmax(0, 1fr) 24px",
                        alignItems: "center",
                        columnGap: "2px",
                        width: "100%",
                        padding: 0,
                      }}
                    >
                      <span
                        style={{
                          color: "#FF7A45",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          lineHeight: 1,
                          textAlign: "center",
                        }}
                      >
                        &#10094;
                      </span>
                      <p
                        style={{
                          fontSize: "1rem",
                          color: "#4A5568",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {tutor.self_intro_quote}
                      </p>
                      <span
                        style={{
                          color: "#FF7A45",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          lineHeight: 1,
                          textAlign: "center",
                        }}
                      >
                        &#10095;
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Learning Packages */}
      {learningPackages.length > 0 && (
        <section className="learning-packages py-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2
                style={{
                  fontWeight: 800,
                  color: "#1A202C",
                  fontSize: "2.5rem",
                  marginBottom: "1rem",
                }}
              >
                Learning Packages
              </h2>
              <div
                style={{
                  width: "80px",
                  height: "3px",
                  backgroundColor: "#7B9EBD",
                  margin: "0 auto",
                }}
              ></div>
            </div>
            <div className="row g-4 mt-4 justify-content-center">
              {learningPackages.map((pkg) => (
                <div key={pkg.id} className="col-lg-6 col-md-6 col-sm-12 mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <img
                      src={pkg.cover_photo}
                      alt="Learning Package Cover"
                      style={{
                        width: "100%",
                        height: "auto",
                        aspectRatio: "16/9",
                        objectFit: "cover",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                        marginBottom: "1.5rem",
                        backgroundColor: "#fff",
                      }}
                    />
                    <a
                      href={pkg.file}
                      className="btn-main"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0.6rem 2rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontSize: "0.85rem",
                      }}
                    >
                      DOWNLOAD PDF
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Inline styles for hover effects and animations */}
      <style>{`
        .moment-photo-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.15) !important;
        }
        .tutor-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 35px rgba(0,0,0,0.08) !important;
          border-color: #003366 !important;
        }
      `}</style>
    </main>
  );
};

export default AiLiteracyTutor;
